// src/pages/StateAdminReport/StateHeadSettlementsReport.jsx
// Layout follows MsbReport/MsbCommissionTransactions: header + info block, filter bar, summary tiles, grid.
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  CircularProgress,
  Alert,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Grid,
  Card,
  CardContent,
  Chip,
  InputAdornment
} from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DataGrid } from '@mui/x-data-grid';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWallet, faDownload, faRefresh, faArrowUp, faArrowDown } from '@fortawesome/free-solid-svg-icons';
import SearchIcon from '@mui/icons-material/Search';
import { jwtDecode } from 'jwt-decode';
import axios from 'axios';
import { format } from 'date-fns';
import { API_BASE_URL } from '../../constants/constants';
import BackButton from '../../components/BackButton';
import { usePermissions } from '../../context/PermissionContext';
import { formatDateTimeIST } from '../../utils/dateFormat';

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(amount) || 0);

// Internal cash refs ('CASH-...') are never shown
const cleanRef = (v) => (!v || v === 'N/A' || /^CASH-/i.test(String(v)) ? '-' : String(v));

const isWithdrawal = (s) => s.entryType === 'WITHDRAWAL' || (!s.entryType && /payout|withdraw/i.test(s.remarks || ''));
const typeLabel = (s) => (isWithdrawal(s) ? 'Withdrawal' : 'Commission');
const statusLabel = (s) => s.status || (isWithdrawal(s) ? 'COMPLETED' : 'SETTLED');
const modeLabel = (s) => (isWithdrawal(s) ? '-' : (s.paymentMode === 'CASH' ? 'CASH' : s.paymentMode ? 'UPI' : '-'));

const daysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d;
};

const StateHeadSettlementsReport = () => {
  const { can } = usePermissions();
  const [userLevel, setUserLevel] = useState(null);
  const [userId, setUserId] = useState('');
  const [settlements, setSettlements] = useState([]);
  const [stateHeadsList, setStateHeadsList] = useState([]);
  const [selectedStateHeadFilter, setSelectedStateHeadFilter] = useState('ALL');
  const [typeFilter, setTypeFilter] = useState('ALL');
  const [staffFilter, setStaffFilter] = useState(''); // State Staff ID
  const [pincodeFilter, setPincodeFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Default date range: last 30 days (Date objects, sent as yyyy-MM-dd)
  const [startDate, setStartDate] = useState(() => daysAgo(30));
  const [endDate, setEndDate] = useState(() => new Date());

  useEffect(() => {
    const token = sessionStorage.getItem('token');
    if (token) {
      try {
        const decoded = jwtDecode(token);
        setUserLevel(decoded.level);
        setUserId(decoded.userId);
      } catch (err) {
        console.error('Invalid token', err);
      }
    }
  }, []);

  const fetchSettlementsReport = useCallback(async () => {
    if (!startDate || !endDate || isNaN(startDate) || isNaN(endDate)) return;
    try {
      setLoading(true);
      setError(null);
      const token = sessionStorage.getItem('token');

      const res = await axios.post(
        `${API_BASE_URL}/stateAdminReport/settlementsReport`,
        {
          startDate: format(startDate, 'yyyy-MM-dd'),
          endDate: format(endDate, 'yyyy-MM-dd')
          // scope (which State Heads) is decided by the server from the login token
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.status === 200 && res.data?.status === 'SUCCESS') {
        const dataList = res.data.settlements || [];
        setSettlements(dataList);

        // State Heads and their live balances come from the server (not derived from the rows in range)
        const heads = Array.isArray(res.data.stateHeads) ? res.data.stateHeads : [];
        setStateHeadsList(heads.map((h) => ({ ...h, label: `${h.id} - ${h.name}` })));
      } else {
        setSettlements([]);
      }
    } catch (err) {
      console.error('Error fetching State Head settlements report:', err);
      setError('Failed to fetch State Head settlements report.');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, userId, userLevel]);

  useEffect(() => {
    if (userLevel !== null) {
      fetchSettlementsReport();
    }
  }, [userLevel, userId, fetchSettlementsReport]);

  // State Head / type / search filters
  const filteredSettlements = useMemo(() => {
    let result = settlements;
    if (selectedStateHeadFilter !== 'ALL') {
      result = result.filter((s) => s.stateHeadId === selectedStateHeadFilter);
    }
    if (typeFilter !== 'ALL') {
      result = result.filter((s) => (typeFilter === 'WITHDRAWAL' ? isWithdrawal(s) : !isWithdrawal(s)));
    }
    if (staffFilter.trim()) {
      const t = staffFilter.trim().toLowerCase();
      result = result.filter((s) => String(s.stateStaffId || '').toLowerCase().includes(t));
    }
    if (pincodeFilter.trim()) {
      const t = pincodeFilter.trim();
      result = result.filter((s) => (s.pincodes || []).some((p) => String(p).includes(t)));
    }
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      result = result.filter((s) =>
        [s.stateHeadId, s.stateHeadName, s.stateHeadMobile, s.stateHeadEmail, cleanRef(s.transactionId), s.remarks]
          .some((v) => String(v || '').toLowerCase().includes(term))
      );
    }
    return result;
  }, [settlements, selectedStateHeadFilter, typeFilter, searchTerm, staffFilter, pincodeFilter]);

  const rows = useMemo(
    () => filteredSettlements.map((s, idx) => ({ ...s, id: s._id || idx, serialNo: idx + 1 })),
    [filteredSettlements]
  );

  // Summary tiles
  const summary = useMemo(() => {
    let withdrawn = 0, withdrawnCount = 0, commission = 0, commissionCount = 0;
    filteredSettlements.forEach((s) => {
      const amt = Number(s.withdrawalAmount) || 0;
      if (isWithdrawal(s)) { withdrawn += amt; withdrawnCount += 1; }
      else { commission += amt; commissionCount += 1; }
    });
    // Commission Balance = current StateAdminCommissionTotal of the selected head / all listed heads
    const scoped = selectedStateHeadFilter === 'ALL' ? stateHeadsList : stateHeadsList.filter((h) => h.id === selectedStateHeadFilter);
    const balance = scoped.reduce((a, h) => a + (Number(h.currentBalance) || 0), 0);
    return { withdrawn, withdrawnCount, commission, commissionCount, balance, heads: scoped.length };
  }, [filteredSettlements, stateHeadsList, selectedStateHeadFilter]);

  // Info block: selected State Head details, else overview
  const headInfo = useMemo(() => {
    if (selectedStateHeadFilter === 'ALL') return null;
    const h = stateHeadsList.find((x) => x.id === selectedStateHeadFilter);
    return h ? { stateHeadId: h.id, stateHeadName: h.name, stateHeadMobile: h.mobile, stateHeadEmail: h.email, currentBalance: h.currentBalance } : null;
  }, [stateHeadsList, selectedStateHeadFilter]);

  // CSV Export (permission gated)
  const handleExportCSV = () => {
    if (!can('stateHead.settlementsReport.export')) return;
    if (filteredSettlements.length === 0) return;

    const headers = [
      'S.No.', 'Date & Time', 'State Head ID', 'State Head Name', 'State Staff ID', 'Pincodes', 'Type', 'Mode',
      'Amount (₹)', 'Reference', 'Remarks', 'Status', 'Current Balance (₹)'
    ];
    const q = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const csvRows = [headers.join(',')];
    filteredSettlements.forEach((s, idx) => {
      csvRows.push([
        idx + 1,
        q(formatDateTimeIST(s.transactionDate)),
        q(s.stateHeadId),
        q(s.stateHeadName),
        q(s.stateStaffId),
        q((s.pincodes || []).join(' ')),
        q(typeLabel(s)),
        q(modeLabel(s)),
        s.withdrawalAmount || 0,
        q(cleanRef(s.transactionId)),
        q(s.remarks),
        q(statusLabel(s)),
        s.currentBalance || 0
      ].join(','));
    });

    const encodedUri = encodeURI('data:text/csv;charset=utf-8,' + csvRows.join('\n'));
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `STATE_HEAD_Settlements_Report_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const columns = [
    { field: 'serialNo', headerName: 'S.No.', width: 80, align: 'center', headerAlign: 'center' },
    { field: 'transactionDate', headerName: 'Date & Time', width: 200, renderCell: (p) => formatDateTimeIST(p.value) },
    {
      field: 'stateHeadName',
      headerName: 'State Head',
      width: 210,
      renderCell: (p) => (
        <Box>
          <Typography variant="body2" fontWeight="bold">{p.value || 'N/A'}</Typography>
          <Typography variant="caption" color="text.secondary" display="block">{p.row.stateHeadId}</Typography>
        </Box>
      )
    },
    { field: 'stateStaffId', headerName: 'State Staff ID', width: 150 },
    { field: 'pincodes', headerName: 'Pincodes', width: 180, valueGetter: (v, row) => (row && row.pincodes ? row.pincodes.join(', ') : '') },
    {
      field: 'entryType',
      headerName: 'Type',
      width: 140,
      renderCell: (p) => {
        const w = isWithdrawal(p.row);
        return (
          <Chip
            icon={<FontAwesomeIcon icon={w ? faArrowDown : faArrowUp} />}
            label={typeLabel(p.row).toUpperCase()}
            size="small"
            color={w ? 'error' : 'success'}
            variant="filled"
          />
        );
      }
    },
    { field: 'paymentMode', headerName: 'Mode', width: 90, renderCell: (p) => modeLabel(p.row) },
    {
      field: 'withdrawalAmount',
      headerName: 'Amount',
      width: 140,
      renderCell: (p) => (
        <Typography color={isWithdrawal(p.row) ? 'error.main' : 'success.main'} fontWeight="bold">
          {isWithdrawal(p.row) ? '- ' : '+ '}{formatCurrency(p.value)}
        </Typography>
      )
    },
    {
      field: 'transactionId',
      headerName: 'Reference',
      width: 190,
      renderCell: (p) => <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{cleanRef(p.value)}</Typography>
    },
    { field: 'remarks', headerName: 'Remarks', width: 240 },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: (p) => <Chip label={statusLabel(p.row)} size="small" color="primary" variant="outlined" />
    },
    { field: 'currentBalance', headerName: 'Balance After', width: 160, renderCell: (p) => formatCurrency(p.value) }
  ];

  return (
    <Box sx={{ position: 'relative', padding: 2 }}>
      <BackButton />
      <br /><br />

      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <div style={{ padding: '20px' }}>
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              <FontAwesomeIcon icon={faWallet} style={{ marginRight: '10px', color: '#1976d2' }} />
              State Head Settlements Report
            </Typography>
          </Box>

          {/* Info block */}
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body1">
              {headInfo ? (
                <>
                  <strong>{headInfo.stateHeadName}</strong> ({headInfo.stateHeadId}
                  {headInfo.stateHeadMobile && headInfo.stateHeadMobile !== 'N/A' ? ` | ${headInfo.stateHeadMobile}` : ''}
                  {headInfo.stateHeadEmail && headInfo.stateHeadEmail !== 'N/A' ? ` | ${headInfo.stateHeadEmail}` : ''}) |{' '}
                  Commission Balance: <strong>{formatCurrency(headInfo.currentBalance)}</strong>
                </>
              ) : (
                <>
                  <strong>All State Heads</strong> ({summary.heads}) | Total Commission Balance:{' '}
                  <strong>{formatCurrency(summary.balance)}</strong>
                </>
              )}
              {' '}| Period: {format(startDate, 'dd/MM/yyyy')} to {format(endDate, 'dd/MM/yyyy')}
            </Typography>
          </Alert>

          {/* Filter Controls */}
          <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="Search"
                placeholder="State Head ID, name, mobile, email, reference"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon color="action" /></InputAdornment> }}
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <FormControl fullWidth>
                <InputLabel>State Head</InputLabel>
                <Select value={selectedStateHeadFilter} label="State Head" onChange={(e) => setSelectedStateHeadFilter(e.target.value)}>
                  <MenuItem value="ALL">All State Heads</MenuItem>
                  {stateHeadsList.map((sh) => (
                    <MenuItem key={sh.id} value={sh.id}>{sh.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={2}>
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={setStartDate}
                renderInput={(params) => <TextField fullWidth {...params} />}
                maxDate={new Date()}
                inputFormat="dd/MM/yyyy"
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={setEndDate}
                renderInput={(params) => <TextField fullWidth {...params} />}
                maxDate={new Date()}
                inputFormat="dd/MM/yyyy"
              />
            </Grid>
            <Grid item xs={12} sm={1}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select value={typeFilter} label="Type" onChange={(e) => setTypeFilter(e.target.value)}>
                  <MenuItem value="ALL">All</MenuItem>
                  <MenuItem value="COMMISSION">Commission</MenuItem>
                  <MenuItem value="WITHDRAWAL">Withdrawal</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={1}>
              <TextField fullWidth label="State Staff ID" value={staffFilter} onChange={(e) => setStaffFilter(e.target.value)} />
            </Grid>
            <Grid item xs={6} sm={1}>
              <TextField fullWidth label="Pincode" value={pincodeFilter} onChange={(e) => setPincodeFilter(e.target.value)} />
            </Grid>
            <Grid item xs={6} sm={1}>
              <Button variant="contained" color="primary" onClick={fetchSettlementsReport}
                startIcon={<FontAwesomeIcon icon={faRefresh} />} fullWidth disabled={loading}>
                Refresh
              </Button>
            </Grid>
            {can('stateHead.settlementsReport.export') && (
              <Grid item xs={6} sm={1}>
                <Button variant="contained" color="success" onClick={handleExportCSV}
                  startIcon={<FontAwesomeIcon icon={faDownload} />} fullWidth disabled={filteredSettlements.length === 0}>
                  CSV
                </Button>
              </Grid>
            )}
          </Grid>

          {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

          {/* Summary Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={3}>
              <Card elevation={2}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h6" color="primary">Commission Balance</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'success.main' }}>{formatCurrency(summary.balance)}</Typography>
                  <Typography variant="body2" color="text.secondary">{summary.heads} State Head(s)</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Card elevation={2}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h6" color="success.main">Commission Settled</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold' }}>{formatCurrency(summary.commission)}</Typography>
                  <Typography variant="body2" color="text.secondary">{summary.commissionCount} transactions</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Card elevation={2}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h6" color="error.main">Withdrawn / Paid Out</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold' }}>{formatCurrency(summary.withdrawn)}</Typography>
                  <Typography variant="body2" color="text.secondary">{summary.withdrawnCount} payouts</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Card elevation={2}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h6" color="primary">Total Records</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold' }}>{filteredSettlements.length}</Typography>
                  <Typography variant="body2" color="text.secondary">of {settlements.length} in range</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Data Grid */}
          <Paper elevation={3} sx={{ height: 600, width: '100%' }}>
            <DataGrid
              rows={rows}
              columns={columns}
              loading={loading}
              disableRowSelectionOnClick
              rowHeight={56}
              pageSizeOptions={[25, 50, 100]}
              initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
              slots={{ noRowsOverlay: () => (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                  <Typography variant="h6" color="text.secondary">No settlement records found</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Try adjusting your date range or filters</Typography>
                </Box>
              ) }}
              sx={{ '& .MuiDataGrid-row:hover': { backgroundColor: 'rgba(25, 118, 210, 0.04)' } }}
            />
          </Paper>
        </div>
      </LocalizationProvider>
    </Box>
  );
};

export default StateHeadSettlementsReport;
